import {
  Box,
  Tab,
  TabList,
  Tabs,
  TabsProps,
  useMediaQuery,
} from '@chakra-ui/react';
import { ReactNode } from 'react';

export interface PillTabsItem {
  name: string;
}

export interface PillTabsProps extends Omit<TabsProps, 'children'> {
  tablist: PillTabsItem[];
  children: ReactNode;
  disabled?: boolean;
  isDisabled?: boolean;
  disabledIndices?: number[];
}

export function PillTabs({
  tablist,
  children,
  disabled,
  isDisabled,
  disabledIndices,
  ...props
}: PillTabsProps) {
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  // Support both disabled and isDisabled props for consistency with Chakra UI
  const isTabDisabled = disabled ?? isDisabled ?? false;

  return (
    <Tabs width="100%" variant="unstyled" {...props}>
      <Box
        w="100%"
        position="relative"
        borderRadius="xl"
        p={1}
        _before={{
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '12px',
          bg: 'azure',
          opacity: 0.12,
          pointerEvents: 'none',
        }}
      >
        <TabList
          overflowX={isMobile ? 'scroll' : 'auto'}
          w="100%"
          gap={2}
          position="relative"
          zIndex={1}
        >
          {tablist.map((item, index) => {
            const isCurrentTabDisabled = Boolean(
              isTabDisabled ||
                (disabledIndices && disabledIndices.includes(index))
            );

            return (
              <Tab
                key={item.name}
                flex={1}
                borderWidth="1px"
                borderColor="azure"
                color="azure"
                bg="white"
                borderRadius="lg"
                _selected={{
                  color: 'white',
                  bg: 'blue',
                  borderColor: 'blue',
                }}
                _disabled={{
                  bg: 'gray.100',
                  color: 'gray.600',
                  borderColor: 'gray.200',
                  cursor: 'not-allowed',
                  opacity: 1,
                }}
                sx={{
                  '&[aria-selected=true]:disabled': {
                    color: 'white',
                    bg: 'blue',
                    borderColor: 'blue',
                    cursor: 'not-allowed',
                    opacity: 1,
                  },
                }}
                whiteSpace="nowrap"
                px={4}
                py={3}
                isDisabled={isCurrentTabDisabled}
                aria-disabled={isCurrentTabDisabled}
              >
                {item.name}
              </Tab>
            );
          })}
        </TabList>
      </Box>
      {children}
    </Tabs>
  );
}
